import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, ShieldAlert, Mail, HelpCircle } from "lucide-react";
import { translations } from "../i18n/translations";
import { useLang } from "../i18n/useLang";

const LOGO =
  "https://customer-assets.emergentagent.com/job_focus-love-app/artifacts/wqa1c5lx_reveal%20logo.png";
const SUPPORT_EMAIL = "support@getrevealapp.com";

export default function AccountDeletion() {
  const lang = useLang();
  const t = (translations[lang] || translations.en).accountDeletion;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t.s3.subjectValue)}`;

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-16" data-testid="account-deletion-page">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/landing"
          data-testid="account-deletion-back-link"
          className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>

        <div className="flex flex-col items-center text-center mb-12">
          <img src={LOGO} alt="Reveal" className="w-24 h-24 object-contain mb-6" data-testid="account-deletion-logo" />
          <h1 className="font-heading text-4xl sm:text-5xl text-reveal-cream mb-3">{t.title}</h1>
          <p className="text-sm text-reveal-cream/45">{t.updated}</p>
        </div>

        <div className="space-y-11">
          {/* Section 1 */}
          <section data-testid="account-deletion-section-1">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> {t.s1.h}
            </h2>
            <ol className="space-y-3">
              {t.s1.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-reveal-cream/75 leading-relaxed">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full border border-reveal-rose/40 text-reveal-rose text-xs flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Section 2 */}
          <section data-testid="account-deletion-section-2">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> {t.s2.h}
            </h2>
            <ul className="space-y-3">
              {t.s2.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-reveal-cream/75 leading-relaxed">
                  <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-reveal-rose" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section data-testid="account-deletion-section-3">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" /> {t.s3.h}
            </h2>
            <p className="text-reveal-cream/75 leading-relaxed mb-4">{t.s3.intro}</p>
            <div className="rounded-2xl border border-reveal-rose/20 bg-reveal-burgundy/40 p-5 space-y-2 text-reveal-cream/80">
              <p>
                <span className="text-reveal-rose">{t.s3.emailLabel}:</span>{" "}
                <a href={mailto} className="text-reveal-cream hover:text-reveal-rose transition-colors underline underline-offset-4" data-testid="account-deletion-email-link">
                  {SUPPORT_EMAIL}
                </a>
              </p>
              <p><span className="text-reveal-rose">{t.s3.subjectLabel}:</span> {t.s3.subjectValue}</p>
              <p><span className="text-reveal-rose">{t.s3.includeLabel}:</span> {t.s3.includeValue}</p>
            </div>
            <p className="text-sm text-reveal-cream/55 mt-4">{t.s3.gdpr}</p>
          </section>

          {/* Section 4 */}
          <section data-testid="account-deletion-section-4">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <HelpCircle className="w-5 h-5" /> {t.s4.h}
            </h2>
            <p className="text-reveal-cream/75 leading-relaxed">
              {t.s4.body}{" "}
              <a href={mailto} className="text-reveal-rose hover:text-reveal-cream transition-colors">
                {SUPPORT_EMAIL}
              </a>
              . {t.s4.respond}
            </p>
          </section>
        </div>

        <div className="mt-14 pt-8 border-t border-reveal-rose/10 text-center">
          <Link
            to="/landing"
            data-testid="account-deletion-home-link"
            className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t.home}
          </Link>
        </div>
      </div>
    </div>
  );
}
